import { useReportLoading } from '../../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { AiTwotoneMail } from 'react-icons/ai';
import { AppVarsContext } from '../../../context/AppVarsContext';
import { lightenHexColor } from '../../../dwi-functions';
import {
    EmailGallerySection,
    EmailInsightsCard,
    EmailMetricGrid,
    EmailTableSection,
    formatEmailDate,
} from '../../shared/emailReportShared';
import {
    getPlainSurfaceCardSx,
    getReportBackdropSx,
    getReportCanvasSx,
} from '../../shared/reportSurfaceStyles';
import { GlassLoadingState, formatReportDateRangeLabel } from '../../shared/reportStatusCards';

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(1)}%`;

const readPreviewHref = (row) => (
    row?.preview_url
    || row?.previewUrl
    || row?.archive_url
    || row?.archiveUrl
    || row?.web_version_url
    || row?.webVersionUrl
    || row?.url
    || row?.raw?.preview_url
    || row?.raw?.previewUrl
    || row?.raw?.archive_url
    || row?.raw?.archiveUrl
    || row?.raw?.web_version_url
    || row?.raw?.webVersionUrl
    || row?.raw?.url
    || ''
);

const SalsaLabsReport = (props) => {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    useReportLoading(loading, props.setLoading);
    const [cid] = useState(sessionStorage.getItem('rid'));
    const theme = useTheme();
    const { appVars, appLoading } = useContext(AppVarsContext);
    const title = props?.title || 'Salsa Labs';
    const dateRangeLabel = formatReportDateRangeLabel(sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate'));

    const fetchEmails = useCallback(async () => {
        if (!appVars?.api_url || !cid) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(`${appVars.api_url}/salsalabs/email/results`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid,
                    sdate: sessionStorage.getItem('sDate'),
                    edate: sessionStorage.getItem('eDate'),
                }),
                redirect: 'follow',
            });
            const data = await res.json();
            const rows = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
            setEmails(rows);

        } catch (error) {
            console.error('Error fetching Salsa Labs email results:', error);
            setEmails([]);
        } finally {
            setLoading(false);
        }
    }, [appVars?.api_url, cid]);

    useEffect(() => {
        if (appLoading) return;
        fetchEmails();
    }, [appLoading, fetchEmails]);

    useEffect(() => {
        const handleStorageChange = () => {
            setTimeout(() => fetchEmails(), 100);
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchEmails]);

    const rows = useMemo(() => emails.map((row, index) => {
        const delivered = safeNum(row?.DELIVERED);
        const bounces = safeNum(row?.BOUNCES);
        const sent = delivered + bounces;
        const opens = safeNum(row?.OPENS);
        const clicks = safeNum(row?.CLICKS);
        const conversions = safeNum(row?.CONVERSIONS);
        const openRate = delivered ? (opens / delivered) * 100 : 0;
        const clickRate = delivered ? (clicks / delivered) * 100 : 0;
        const ctor = opens ? (clicks / opens) * 100 : 0;
        const conversionRate = delivered ? (conversions / delivered) * 100 : 0;
        const clickToConversion = clicks ? (conversions / clicks) * 100 : 0;
        const bounceRate = sent ? (bounces / sent) * 100 : 0;
        return {
            id: row?.EMAIL_ID || `email-${index}`,
            name: row?.EMAIL_NAME || `Email ${index + 1}`,
            sendDate: row?.SEND_DATE,
            previewText: row?.PREVIEW_TEXT || row?.DESCRIPTION || '',
            sent,
            delivered,
            opens,
            clicks,
            conversions,
            bounces,
            openRate,
            clickRate,
            ctor,
            conversionRate,
            clickToConversion,
            bounceRate,
            raw: row,
        };
    }), [emails]);

    const summary = useMemo(() => {
        const totals = rows.reduce((acc, row) => {
            acc.sent += row.sent;
            acc.delivered += row.delivered;
            acc.opens += row.opens;
            acc.clicks += row.clicks;
            acc.conversions += row.conversions;
            acc.bounces += row.bounces;
            return acc;
        }, {
            sent: 0,
            delivered: 0,
            opens: 0,
            clicks: 0,
            conversions: 0,
            bounces: 0,
        });

        return {
            emails: rows.length,
            totals,
            openRate: totals.delivered ? (totals.opens / totals.delivered) * 100 : 0,
            clickRate: totals.delivered ? (totals.clicks / totals.delivered) * 100 : 0,
            ctor: totals.opens ? (totals.clicks / totals.opens) * 100 : 0,
            conversionRate: totals.delivered ? (totals.conversions / totals.delivered) * 100 : 0,
            clickToConversion: totals.clicks ? (totals.conversions / totals.clicks) * 100 : 0,
            bounceRate: totals.sent ? (totals.bounces / totals.sent) * 100 : 0,
        };
    }, [rows]);

    const rankings = useMemo(() => ({
        topOpen: [...rows].sort((a, b) => b.openRate - a.openRate)[0] || null,
        topClick: [...rows].sort((a, b) => b.clickRate - a.clickRate)[0] || null,
        topConversions: [...rows].sort((a, b) => b.conversions - a.conversions || b.clicks - a.clicks)[0] || null,
        topBounce: [...rows].sort((a, b) => b.bounceRate - a.bounceRate)[0] || null,
    }), [rows]);

    const insightItems = useMemo(() => {
        const items = [];

        if (rankings.topConversions?.conversions > 0) {
            items.push({
                tone: 'success',
                label: 'Conversion Leader',
                text: `${rankings.topConversions.name} led Salsa conversions with ${formatNumber(rankings.topConversions.conversions)} and converted ${formatPercent(rankings.topConversions.clickToConversion)} of its clicks.`,
            });
        }

        if (rankings.topBounce?.bounceRate >= 5) {
            items.push({
                tone: 'error',
                label: 'Deliverability Risk',
                text: `${rankings.topBounce.name} had the highest bounce rate at ${formatPercent(rankings.topBounce.bounceRate)}.`,
            });
        }

        if (summary.clickRate < 1) {
            items.push({
                tone: 'warning',
                label: 'CTA Weakness',
                text: `Overall click rate is only ${formatPercent(summary.clickRate)}. The emails are getting delivered, but not enough traffic is moving through.`,
            });
        }

        if (summary.clickToConversion > 0) {
            items.push({
                tone: 'default',
                label: 'Click-To-Conversion',
                text: `Across the range, ${formatPercent(summary.clickToConversion)} of clicks turned into conversions.`,
            });
        }

        return items.slice(0, 4);
    }, [rankings.topBounce, rankings.topConversions, summary.clickRate, summary.clickToConversion]);

    const metricItems = useMemo(() => ([
        { label: 'Emails', value: formatNumber(summary.emails), helper: 'In selected range' },
        { label: 'Sent', value: formatNumber(summary.totals.sent), helper: 'Delivered plus bounces' },
        { label: 'Delivered', value: formatNumber(summary.totals.delivered), helper: 'Successful deliveries' },
        { label: 'Opens', value: formatNumber(summary.totals.opens), helper: `Open Rate ${formatPercent(summary.openRate)}` },
        { label: 'Clicks', value: formatNumber(summary.totals.clicks), helper: `Click Rate ${formatPercent(summary.clickRate)}` },
        { label: 'CTOR', value: formatPercent(summary.ctor), helper: 'Clicks from opens' },
        { label: 'Conversions', value: formatNumber(summary.totals.conversions), helper: `Conversion ${formatPercent(summary.conversionRate)}` },
        { label: 'Bounce Rate', value: formatPercent(summary.bounceRate), helper: `${formatNumber(summary.totals.bounces)} bounces` },
    ]), [summary]);

    const galleryRows = useMemo(() => rows.map((row) => {
        const previewHref = readPreviewHref(row);
        return {
            id: row.id,
            name: row.name,
            sendDate: row.sendDate,
            previewText: row.previewText,
            previewHref,
            captureUrl: previewHref,
            placeholder: 'SL',
            metricItems: [
                { label: 'Delivered', value: formatNumber(row.delivered) },
                { label: 'Opens', value: `${formatNumber(row.opens)} · ${formatPercent(row.openRate)}` },
                { label: 'Clicks', value: `${formatNumber(row.clicks)} · ${formatPercent(row.clickRate)}` },
                { label: 'Conversions', value: row.clicks ? `${formatNumber(row.conversions)} · ${formatPercent(row.clickToConversion)}` : formatNumber(row.conversions) },
            ],
        };
    }), [rows]);

    const sortedRows = useMemo(() => [...rows].sort((a, b) => {
        const aDate = new Date(a.sendDate).getTime() || 0;
        const bDate = new Date(b.sendDate).getTime() || 0;
        return bDate - aDate;
    }), [rows]);

    if (loading) {
        return (
            <Box sx={getReportCanvasSx(theme)}>
                <Box sx={getReportBackdropSx(theme)} />
                <GlassLoadingState
                    icon={<AiTwotoneMail size={34} />}
                    title={`Loading ${title}`}
                    subtitle={dateRangeLabel ? `Pulling delivery, engagement, and conversion activity for ${dateRangeLabel}.` : 'Pulling delivery, engagement, and conversion activity for the selected range.'}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 0, flex: 1 }}>
            <Box sx={getReportCanvasSx(theme)}>
                <Box sx={getReportBackdropSx(theme)} />
                {rows.length > 0 ? (
                    <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                        <EmailMetricGrid items={metricItems} />

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' } }}>
                            <Card sx={getPlainSurfaceCardSx(theme)}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Email Engagement Funnel
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, mb: 1.5 }}>
                                        The same delivery-to-click flow used across the email reports, with conversions added for Salsa Labs.
                                    </Typography>

                                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                                        {[
                                            { label: 'Delivered', value: summary.totals.delivered, denom: summary.totals.sent, color: theme.palette.primary.main },
                                            { label: 'Opened', value: summary.totals.opens, denom: summary.totals.delivered, color: theme.palette.secondary.main },
                                            { label: 'Clicked', value: summary.totals.clicks, denom: summary.totals.delivered, color: theme.palette.success.main },
                                            { label: 'Converted', value: summary.totals.conversions, denom: summary.totals.clicks, color: theme.palette.info.main },
                                        ].map((item) => {
                                            const percent = item.denom ? (item.value / item.denom) * 100 : 0;
                                            return (
                                                <Box key={item.label} sx={{ display: 'grid', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="body2">{item.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatNumber(item.value)} · {formatPercent(percent)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: theme.palette.action.hover }}>
                                                        <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` }} />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </CardContent>
                            </Card>

                            <EmailInsightsCard
                                title="What Needs Attention"
                                subtitle="Salsa Labs email and conversion callouts for the selected range."
                                items={insightItems}
                            />
                        </Box>

                        <EmailGallerySection
                            providerLabel="Salsa Labs"
                            title="Emails In Range"
                            subtitle="Thumbnail cards for each Salsa Labs email. Previews appear when the API returns a usable email URL."
                            rows={galleryRows}
                        />

                        <EmailTableSection
                            title="Email Performance"
                            subtitle="Delivery, engagement, and conversion detail for each Salsa Labs email."
                            rows={sortedRows}
                            emptyMessage="No Salsa Labs emails were found in the selected date range."
                            columns={[
                                {
                                    key: 'name',
                                    label: 'Email',
                                    render: (row) => (
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {row.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatEmailDate(row.sendDate)}
                                            </Typography>
                                        </Box>
                                    ),
                                },
                                { key: 'sent', label: 'Sent', align: 'right', render: (row) => formatNumber(row.sent) },
                                { key: 'delivered', label: 'Delivered', align: 'right', render: (row) => formatNumber(row.delivered) },
                                { key: 'opens', label: 'Opens', align: 'right', render: (row) => formatNumber(row.opens) },
                                { key: 'clicks', label: 'Clicks', align: 'right', render: (row) => formatNumber(row.clicks) },
                                { key: 'conversions', label: 'Conversions', align: 'right', render: (row) => formatNumber(row.conversions) },
                                { key: 'openRate', label: 'Open Rate', align: 'right', render: (row) => formatPercent(row.openRate) },
                                { key: 'clickRate', label: 'Click Rate', align: 'right', render: (row) => formatPercent(row.clickRate) },
                                { key: 'clickToConversion', label: 'Click → Conv', align: 'right', render: (row) => row.clicks ? formatPercent(row.clickToConversion) : '—' },
                            ]}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            m: 'auto',
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            background: lightenHexColor(theme.palette.primary.main, 80),
                        }}
                    >
                        <Box display="flex" flexDirection="column" p={2} alignItems="center" justifyContent="center" width="100%" gap={1}>
                            <AiTwotoneMail size={70} color={theme.palette.primary.main} />
                            <Typography>No Emails</Typography>
                            <Typography>{sessionStorage.getItem('sDate')} and {sessionStorage.getItem('eDate')}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SalsaLabsReport;
